import { useCallback, useEffect, useState } from 'react';

export interface LegalDocumentState<T> {
  /** The document in force, once it has been read. */
  document: T | null;
  /** True when it could not be read: a page that cannot show the text cannot take the acceptance. */
  unavailable: boolean;
  /** Reads it again (the version in force moved while the page was open). */
  reload: () => void;
}

/** A legal document in force (the privacy notice, the Terms and Conditions), read once and on demand. */
export function useLegalDocument<T>(read: () => Promise<T>): LegalDocumentState<T> {
  const [state, setState] = useState<{ document: T | null; unavailable: boolean }>({
    document: null,
    unavailable: false,
  });
  const [round, setRound] = useState(0);

  useEffect(() => {
    let cancelled = false;
    read()
      .then((document) => {
        if (!cancelled) setState({ document, unavailable: false });
      })
      .catch(() => {
        if (!cancelled) setState({ document: null, unavailable: true });
      });
    return () => {
      cancelled = true;
    };
    // `read` is a module-level service call: reading again is asked for with `reload`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  const reload = useCallback(() => setRound((n) => n + 1), []);
  return { ...state, reload };
}
